<template>
    <component :is="as" :class="['base-card', themeClasses[theme], isInteractive && 'is-interactive', paddingClasses[padding], radiusClasses[radius], elevated && 'shadow-card']">
        <slot />
    </component>
</template>

<script lang="ts">
export type BaseCardTheme = "default" | "muted" | "accent" | "cream" | "cream-muted";
export type BaseCardPadding = "none" | "sm" | "md" | "lg";
export type BaseCardRadius = "md" | "lgr" | "lg" | "xl";
export type BaseCardAs = "div" | "a" | "button" | "article" | "section";

interface BaseCardProps {
    theme?: BaseCardTheme;
    padding?: BaseCardPadding;
    radius?: BaseCardRadius;
    // Forces interactive styling on non-link/button tags. `as="a"` and
    // `as="button"` are interactive by default, so the prop is rarely needed.
    interactive?: boolean;
    // Soft lift (shadow-card) for cards that float over the page, e.g. in-chat tool cards.
    elevated?: boolean;
    as?: BaseCardAs;
}
</script>

<script setup lang="ts">
import { computed } from "vue";

const { theme = "default", padding = "md", radius = "lg", interactive = false, elevated = false, as = "div" } = defineProps<BaseCardProps>();

const themeClasses: Record<BaseCardTheme, string> = {
    default: "theme-default",
    muted: "theme-muted",
    accent: "theme-accent",
    cream: "theme-cream",
    "cream-muted": "theme-cream-muted",
};

const paddingClasses: Record<BaseCardPadding, string> = {
    none: "",
    sm: "p-4",
    md: "p-5",
    lg: "p-6",
};

// `lg` is the default corner and the one a theme can restyle via --radius-card.
const radiusClasses: Record<BaseCardRadius, string> = {
    md: "rounded-lg",
    lgr: "rounded-lgr",
    lg: "rounded-card",
    xl: "rounded-2xl",
};

// `as="a"` and `as="button"` carry interactive semantics; the prop only needs
// to be set to opt a plain container (div/article/section) into hover styling.
const isInteractive = computed(() => interactive || as === "a" || as === "button");
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .base-card {
        @apply block;
    }

    .base-card.theme-default {
        @apply border border-card-border bg-surface-raised;
    }

    .base-card.theme-muted {
        @apply border border-transparent bg-surface-sunken;
    }

    .base-card.theme-accent {
        @apply border border-card-accent-border bg-card-accent-bg;
    }

    .base-card.theme-cream {
        @apply border border-card-cream-border bg-surface-raised;
    }

    .base-card.theme-cream-muted {
        @apply border border-transparent bg-card-cream-muted-bg;
    }

    .base-card.is-interactive {
        @apply cursor-pointer transition-[colors,shadow] hover:shadow-sm;
    }
}
</style>
